import { ExclamationCircleOutlined } from '../../../components/icons'
import { EmptyState } from '../../../components/ui/EmptyState'
import { DFM_SEVERITY_LABEL } from '../dfm'
import type { DfmFinding } from '../types'

export interface DfmDiscrepancyListProps {
  readonly findings: readonly DfmFinding[]
  /** Rendered above the list as "DISCREPANCIES (n)" (`dc.html:4807`,
   * `previewPart.issueCount`). Pass `false` to omit the heading when the
   * caller already renders its own (e.g. a modal title). */
  readonly heading?: boolean
}

/**
 * Findings-list rendering pattern (UID-151/E1) — used by the Part detail
 * preview drawer (`PartDetailPage.tsx`) and `PartConfiguratorShell.tsx`.
 * (It was also used by the uploads vertical's `DfmFeedbackModal`, which was
 * removed with the Staging "View DFM" entry point — the design has no such
 * control. This component is unaffected.)
 * Renders `dc.html:4896-4903`'s discrepancy row: an icon (never a
 * color-only dot — WCAG 1.4.1) + message + optional detail, as a semantic
 * list. The "DISCREPANCIES (n)" count is wrapped in `aria-live="polite"` so
 * a count that changes after the initial render (e.g. a re-analysis) is
 * announced.
 */
export function DfmDiscrepancyList({ findings, heading = true }: DfmDiscrepancyListProps) {
  if (findings.length === 0) {
    return (
      <EmptyState title="No discrepancies" detail="This part passed manufacturability review." />
    )
  }

  return (
    <div className="dfm-discrepancy-panel">
      {heading && (
        <p className="dfm-discrepancy-heading" aria-live="polite">
          DISCREPANCIES ({findings.length})
        </p>
      )}
      <ul className="dfm-discrepancy-list">
        {findings.map((finding, index) => (
          // Findings are plain strings on the wire (no stable id) — index is
          // safe here since the list is re-rendered wholesale, never reordered
          // or partially patched in place.
          <li key={`${finding.label}-${index}`} className="dfm-discrepancy-item">
            <ExclamationCircleOutlined aria-hidden="true" className="dfm-discrepancy-icon" />
            <div>
              <p className="dfm-discrepancy-label">
                <span className="sr-only">{DFM_SEVERITY_LABEL[finding.severity]}: </span>
                {finding.label}
              </p>
              {finding.detail && <p className="dfm-discrepancy-detail">{finding.detail}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
